import { voiceConfigStore } from '@/voice/state';
import { SETTINGS_ACTION_TYPES } from './settings-actions-copy';
import { announceMeetingUpdated } from '@/workspace/meeting-events';
import { useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { useActionVisibility } from './use-action-visibility';
import { pendingActionScope, pendingActionStore } from './pending-action-store';
import { useAccountSettings, settingsChangeFromTool } from '@/lib/account-settings';
import { useCompany } from '@/company/context';
import { useActionCardGrant } from './use-action-card-grant';
import { useOptionalWorkspace } from '@/workspace/context';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionAccessCopy from '@/i18n/ns/connection-access';
import { actionCopy } from './action-copy';
import { actionSurfaceCopy } from './action-surface-copy';
import { actionRequest } from './action-api';
import { createActionOutcomeSpeaker } from '@/voice/confirm/action-outcome';
import { actionResultTarget } from './action-result-target';
import { announceKnowledgePagesChanged } from '@/workspace/knowledge-agent-events';
import { publishActionOutcome } from './activity-store';
import { APP_BUILD_DECISION_TOOLS, noteAppBuildDecision } from './app-build-decisions';
import { actionUsesCanvas } from './action-layout';
import type { ActionOrigin, ActionPointer, ActionRestage, ActionView } from './action-confirmation';
import {
  ACTION_PRESENTATION_FINISHED,
  ACTION_TURN_CHANGED,
  actionTurnRunning,
  notifyActionPresentationFinished,
} from './action-presentation';

export function useActionConfirmation(
  pointer: ActionPointer,
  mode: 'compact' | 'canvas',
  fresh: boolean,
  origin?: ActionOrigin,
  onRestaged?: (restage: ActionRestage) => void,
) {
  const [action, setAction] = useState<ActionView | null>(null);
  const [loadError, setLoadError] = useState<{ status?: number; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const generation = useRef(0);
  const presentation = useActionVisibility();
  const registration = useRef(Symbol());
  const company = useCompany();
  const busyRef = useRef(false);
  const freshCompletionIds = useRef(new Set<string>());
  const appliedResultIds = useRef(new Set<string>());
  const outcomeSpeaker = useMemo(() => createActionOutcomeSpeaker(), []);
  const opened = useRef(new Set<string>());
  const focusReceipt = useRef(false);
  const wasFresh = useRef(fresh);
  if (fresh) wasFresh.current = true;
  const settings = useAccountSettings();
  const workspace = useOptionalWorkspace();
  const copy = useChatCopy();
  const accessCopy = useNamespaceCopy(connectionAccessCopy);
  const locale = useChatInterfaceLocale();
  const actionText = actionCopy(locale);
  const surfaceCopy = actionSurfaceCopy(locale);
  const companyId = company.data?.company?.id ?? '';
  const identity = `${pointer.action.id}:${companyId}:${workspace?.threadId ?? ''}:${settings?.userId ?? ''}`;
  const inScope =
    (!workspace || action?.threadId === workspace.threadId) &&
    (!settings || action?.account === settings.userId) &&
    (!companyId || action?.organizationId === companyId);
  const requestedBuild =
    pointer.status === 'build_requested' || action?.approval?.method === 'request';
  const grants = useActionCardGrant(
    !requestedBuild &&
      inScope &&
      presentation.visible &&
      (!action || !actionUsesCanvas(action) || mode === 'canvas')
      ? action
      : null,
    identity,
  );
  const acknowledge = grants.acknowledge;
  useEffect(() => {
    const generationId = ++generation.current;
    let current = true;
    busyRef.current = false;
    setBusy(false);
    setEdit(false);
    setAction(null);
    setLoadError(null);
    setNotice(null);
    setUncertain(false);
    focusReceipt.current = false;
    grants.reset();
    wasFresh.current = fresh;
    const isCurrent = () => current && generation.current === generationId;
    void actionRequest<{ action: ActionView }>(pointer.action.id).then(
      ({ action: loaded }) => {
        if (isCurrent()) {
          grants.reset();
          if (
            loaded.status === 'executed' &&
            pointer.status === 'build_requested' &&
            wasFresh.current
          )
            freshCompletionIds.current.add(loaded.id);
          setAction(loaded);
          setDraft(loaded.args);
          setEdit(mode === 'canvas' && loaded.editing === true);
        }
      },
      (error: Error & { status?: number }) => {
        if (isCurrent()) setLoadError({ status: error.status, message: error.message });
      },
    );
    return () => {
      current = false;
      outcomeSpeaker.cancel();
      grants.reset();
    };
  }, [identity, pointer.action.id]);
  useEffect(() => () => outcomeSpeaker.dispose(), [outcomeSpeaker]);
  useEffect(() => {
    if (
      !action ||
      requestedBuild ||
      mode !== 'compact' ||
      !wasFresh.current ||
      action.status !== 'pending' ||
      !workspace
    )
      return;
    if (workspace.canvas || window.innerWidth <= 640) return;
    if (actionUsesCanvas(action) && !opened.current.has(action.id)) {
      opened.current.add(action.id);
      workspace.openCanvas({ kind: 'action', id: action.id }, { auto: true });
    }
  }, [action, mode, workspace, requestedBuild]);
  useEffect(() => {
    if (!action || (action.status !== 'pending' && action.status !== 'running')) return;
    const generationId = generation.current;
    let current = true;
    let timer: ReturnType<typeof setInterval> | undefined;
    let loading = false;
    const refresh = async () => {
      if (loading || !current) return;
      loading = true;
      try {
        const { action: latest } = await actionRequest<{ action: ActionView }>(action.id);
        if (!current || generationId !== generation.current) return;
        if (latest.status === 'executed') freshCompletionIds.current.add(latest.id);
        if (
          action.id !== latest.id ||
          action.digest !== latest.digest ||
          action.status !== latest.status ||
          action.editing !== latest.editing
        )
          grants.reset();
        setAction((current) =>
          current?.id === latest.id && current.status !== 'pending' && current.status !== 'running'
            ? current
            : latest,
        );
        if (!edit) setDraft(latest.args);
        if (latest.status === 'pending' && !requestedBuild) acknowledge(latest);
      } catch {
        /* An unavailable refresh never authorizes or retries execution. */
      } finally {
        loading = false;
      }
    };
    const onFinished = (event: Event) => {
      const threadId = (event as CustomEvent<{ threadId?: string }>).detail?.threadId;
      if (threadId !== action.threadId) return;
      grants.reset();
      void refresh();
    };
    const polling = () => {
      if (timer) clearInterval(timer);
      if (actionTurnRunning(action.threadId)) timer = setInterval(() => void refresh(), 1000);
    };
    const onTurnChanged = (event: Event) => {
      if ((event as CustomEvent<{ threadId: string }>).detail.threadId === action.threadId)
        polling();
    };
    polling();
    window.addEventListener(ACTION_PRESENTATION_FINISHED, onFinished);
    window.addEventListener(ACTION_TURN_CHANGED, onTurnChanged);
    return () => {
      current = false;
      if (timer) clearInterval(timer);
      window.removeEventListener(ACTION_PRESENTATION_FINISHED, onFinished);
      window.removeEventListener(ACTION_TURN_CHANGED, onTurnChanged);
    };
  }, [action?.id, action?.status, edit, presentation.visible, requestedBuild]);
  useEffect(() => {
    if (
      !action ||
      action.status !== 'executed' ||
      !freshCompletionIds.current.has(action.id) ||
      appliedResultIds.current.has(action.id)
    )
      return;
    appliedResultIds.current.add(action.id);
    const change = settingsChangeFromTool(action.tool, action.result);
    if (change && settings && change.account === settings.userId)
      settings.apply(change, { toolCallId: action.id, resumed: false });
    if (workspace) {
      void workspace.refresh();
      if (action.tool === 'add_meeting_note') announceMeetingUpdated();
      if (
        action.tool === 'run_agent' &&
        workspace.threadId === action.threadId &&
        action.result &&
        typeof action.result === 'object' &&
        'threadId' in action.result &&
        typeof action.result.threadId === 'string'
      )
        workspace.openThread(action.result.threadId);
      const destination = actionResultTarget(action);
      if (
        destination &&
        pointer.status === 'build_requested' &&
        action.approval?.method === 'request' &&
        wasFresh.current &&
        inScope &&
        !workspace.readOnly &&
        !workspace.canvas &&
        window.innerWidth > 640 &&
        !opened.current.has(action.id)
      ) {
        opened.current.add(action.id);
        workspace.openCanvas(destination, { auto: true });
      }
      if (
        destination &&
        mode === 'canvas' &&
        workspace.canvas?.kind === 'action' &&
        (workspace.canvas.id === action.id || workspace.canvas.id === pointer.action.id)
      )
        workspace.openCanvas(destination, { replace: true, committed: true });
      else if (action.tool === 'suggest_page_edits') {
        // The suggestion is made: its page opens beside this chat with it in
        // focus, so a publisher accepts with one tap. Never over other work.
        announceKnowledgePagesChanged();
        if (destination && workspace.view === 'chat' && workspace.threadId === action.threadId)
          workspace.openCanvas(destination, { auto: true });
      }
    }
    // An App builder decision changes the App, never the company: reloading the company would only
    // hold every send (the chat's own continuation too) until it returns.
    if (
      Object.hasOwn(SETTINGS_ACTION_TYPES, action.tool) &&
      !APP_BUILD_DECISION_TOOLS.has(action.tool)
    )
      void company.refresh();
    if (['update_company_speech_settings', 'update_company_voice_settings'].includes(action.tool))
      void voiceConfigStore.refresh();
  }, [action, settings, workspace, company, mode, inScope, pointer.status]);
  useEffect(() => {
    if (!action) return;
    if (origin)
      publishActionOutcome(
        action.threadId,
        origin.messageId,
        origin.toolCallId,
        action.status === 'executed'
          ? 'complete'
          : action.status === 'pending'
            ? requestedBuild
              ? 'running'
              : 'approval'
            : action.status === 'running'
              ? 'running'
              : action.status === 'failed'
                ? 'error'
                : 'cancelled',
        action.result,
      );
    if (focusReceipt.current && action.status !== 'pending' && action.status !== 'running') {
      focusReceipt.current = false;
      presentation.element
        ?.querySelector<HTMLElement>('.pc-action-receipt')
        ?.focus({ preventScroll: true });
    }
  }, [action?.id, action?.status, origin?.messageId, presentation.element, requestedBuild]);
  const scope = pendingActionScope(
    settings?.userId ?? workspace?.user?.id,
    company.data?.company?.id,
    workspace?.threadId,
  );
  useEffect(() => {
    const key = registration.current;
    if (
      action &&
      !requestedBuild &&
      scope &&
      action.threadId === workspace?.threadId &&
      action.account === (settings?.userId ?? workspace?.user?.id)
    )
      pendingActionStore.put(key, {
        scope,
        action,
        element: presentation.element,
        visible: presentation.visible,
        presented: grants.ready,
      });
    else pendingActionStore.remove(key);
    return () => pendingActionStore.remove(key);
  }, [scope, action, presentation.element, presentation.visible, grants.ready, requestedBuild]);
  const checkAgain = async () => {
    if (!action || busyRef.current) return;
    const generationId = generation.current;
    busyRef.current = true;
    setBusy(true);
    try {
      const { action: latest } = await actionRequest<{ action: ActionView }>(action.id);
      if (generation.current !== generationId) return;
      if (latest.status === 'executed') freshCompletionIds.current.add(latest.id);
      setAction(latest);
      setUncertain(false);
      setNotice(null);
      if (!edit) setDraft(latest.args);
    } catch {
      if (generation.current === generationId) setNotice(surfaceCopy.uncertain);
    } finally {
      if (generation.current === generationId) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  useEffect(() => {
    if (
      mode !== 'canvas' ||
      !action ||
      workspace?.canvas?.kind !== 'action' ||
      (workspace.canvas.id !== action.id && workspace.canvas.id !== pointer.action.id)
    )
      return;
    workspace.setCanvasDirty(edit);
    return () => workspace.setCanvasDirty(false);
  }, [edit, mode, action?.id, workspace?.canvas?.id]);
  const mutate = async (
    operation: 'approve' | 'reject' | 'restage' | 'editing',
    args?: Record<string, unknown>,
    event?: SyntheticEvent<HTMLButtonElement>,
  ) => {
    const cardToken = grants.token();
    if (
      !action ||
      requestedBuild ||
      busyRef.current ||
      uncertain ||
      action.status !== 'pending' ||
      (operation === 'approve' && !cardToken) ||
      (!action.canDecide && operation !== 'restage')
    )
      return;
    if (operation === 'approve' && event?.nativeEvent.isTrusted !== true) return;
    const actionId = action.id;
    const generationId = generation.current;
    const isCurrent = () => generation.current === generationId;
    const preparedOutcome =
      operation === 'approve'
        ? outcomeSpeaker.prepare({ kind: 'action', id: action.id }, action.digest, action.threadId)
        : null;
    busyRef.current = true;
    grants.reset();
    setBusy(true);
    setNotice(null);
    try {
      const { action: next } = await actionRequest<{ action: ActionView }>(
        action.id,
        `/${operation}`,
        'POST',
        {
          digest: action.digest,
          ...(operation === 'restage' ? { args } : {}),
          ...(operation === 'approve' ? { cardToken } : {}),
        },
      );
      if (isCurrent()) {
        if (operation === 'approve' && next.status === 'executed')
          freshCompletionIds.current.add(next.id);
        if (operation === 'approve' || operation === 'reject') {
          focusReceipt.current = true;
          noteAppBuildDecision(next);
        }
        if (next.digest !== action.digest || next.status !== 'pending' || operation === 'editing') {
          grants.reset();
        }
        setAction(next);
        setDraft(next.args);
        setEdit(operation === 'editing');
        setUncertain(false);
        if (operation === 'approve') {
          if (next.status === 'executed' || next.status === 'failed') {
            preparedOutcome?.settle({ id: next.id, digest: next.digest, status: next.status });
          } else preparedOutcome?.cancel();
        }
        notifyActionPresentationFinished(next.threadId);
        if (next.status === 'pending' && (operation === 'editing' || next.digest !== action.digest))
          acknowledge(next);
        if (operation === 'restage' && workspace) {
          // Provenance for the "Updated with your edits" note comes from this
          // response only: the exact request the server created from the edit.
          if (next.id !== actionId && next.status === 'pending')
            onRestaged?.({ from: actionId, to: next.id });
          workspace.openCanvas({ kind: 'action', id: next.id }, { replace: true, committed: true });
        }
      }
    } catch (error) {
      preparedOutcome?.cancel();
      if (!isCurrent()) return;
      const status = (error as Error & { status?: number }).status;
      setUncertain(true);
      setNotice(
        (error as Error).message === 'connection_write_not_enabled'
          ? formatCopy(accessCopy.cardWriteNeeded, {
              name: action?.decision.connection?.name ?? '',
            })
          : status === 409
            ? copy.tryAgain
            : copy.toolError,
      );
      try {
        const { action: refreshed } = await actionRequest<{ action: ActionView }>(actionId);
        if (isCurrent()) {
          grants.reset();
          setAction(refreshed);
          setDraft(refreshed.args);
          setUncertain(status !== 409 || refreshed.status !== 'pending');
          if (refreshed.status === 'pending') acknowledge(refreshed);
        }
      } catch {
        /* fail closed */
      }
    } finally {
      if (isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  // An inline editor that restages and approves within one click (the email
  // draft card) adopts the action the server returned, as mutate does.
  const adopt = (next: ActionView) => {
    grants.reset();
    if (next.status === 'executed') freshCompletionIds.current.add(next.id);
    setAction(next);
    setDraft(next.args);
    setUncertain(false);
    setNotice(null);
    notifyActionPresentationFinished(next.threadId);
    if (next.status === 'pending') acknowledge(next);
  };
  return {
    action,
    requestedBuild,
    origin,
    presentation,
    loadError,
    busy,
    canApprove: grants.ready,
    cardToken: grants.token,
    adopt,
    edit,
    draft,
    setDraft,
    notice,
    uncertain,
    mutate,
    workspace,
    copy,
    actionText,
    surfaceCopy,
    locale,
    checkAgain,
  };
}
